import axios from 'axios';
import { baseURL } from '../Constants';

const url = `${baseURL}/RatingAnalysis`;

export interface RatingChangePlayer {
    steamId: string;
    playerName: string;
    teamName: string;
    division: string;
    rating20: number;
    rating21: number;
    ratingChange: number;
    gamesPlayed: number;
    roundsPlayed: number;
}

export interface Rating30ChangePlayer {
    steamId: string;
    playerName: string;
    teamName: string;
    division: string;
    rating21: number;
    rating30: number;
    ratingChange: number;
    gamesPlayed: number;
    roundsPlayed: number;
}

export interface Rating30ChangeResponse {
    season: number;
    division: string;
    showingTop: boolean;
    description: string;
    players: Rating30ChangePlayer[];
}

export interface SwingPlayer {
    steamId: string;
    playerName: string;
    teamName: string;
    division: string;
    swingPct: number;
    rating30: number;
    gamesPlayed: number;
    roundsPlayed: number;
}

export interface SwingResponse {
    season: number;
    division: string;
    showingTop: boolean;
    description: string;
    players: SwingPlayer[];
}

export interface CTSidePlayer {
    steamId: string;
    playerName: string;
    teamName: string;
    division: string;
    ctRating21: number;
    ctRating2: number;
    ctadr: number;
    ctkd: number;
    ctkast: number;
    ctKills: number;
    ctDeaths: number;
    ctRoundsPlayed: number;
    ctFirstKills: number;
    ctOpeningDuelWinRate: number;
}

export interface TSidePlayer {
    steamId: string;
    playerName: string;
    teamName: string;
    division: string;
    tRating21: number;
    tRating2: number;
    tadr: number;
    tkd: number;
    tkast: number;
    tKills: number;
    tDeaths: number;
    tRoundsPlayed: number;
    tFirstKills: number;
    tEntryKills: number;
    tOpeningDuelWinRate: number;
}

export interface MultiKillPlayer {
    steamId: string;
    playerName: string;
    teamName: string;
    division: string;
    totalMatches: number;
    totalKills: number;
    twoKillRounds: number;
    threeKillRounds: number;
    fourKillRounds: number;
    fiveKillRounds: number;
    totalMultiKillRounds: number;
    multiKillScore: number;
    multiKillsPerMatch: number;
}

export interface AcePlayer {
    steamId: string;
    playerName: string;
    teamName: string;
    division: string;
    aces: number;
    totalMatches: number;
    acesPerMatch: number;
}

export interface RatingChangeResponse {
    season: number;
    division: string;
    showingTop: boolean;
    description: string;
    players: RatingChangePlayer[];
}

export interface CTSideResponse {
    season: number;
    division: string;
    showingTop: boolean;
    description: string;
    players: CTSidePlayer[];
}

export interface TSideResponse {
    season: number;
    division: string;
    showingTop: boolean;
    description: string;
    players: TSidePlayer[];
}

export interface MultiKillResponse {
    season: number;
    division: string;
    showingTop: boolean;
    description: string;
    multiKillers: MultiKillPlayer[];
    aceLeaders: AcePlayer[];
}

export const fetchRatingChangeLeaders = async (
    season: number,
    division: string,
    top: boolean = true
): Promise<RatingChangeResponse> => {
    try {
        const response = await axios.get(`${url}/rating-change/${season}/${division}`, {
            params: { top }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching rating change leaders:', error);
        throw error;
    }
};

export const fetchRating30ChangeLeaders = async (
    season: number,
    division: string,
    top: boolean = true
): Promise<Rating30ChangeResponse> => {
    try {
        const response = await axios.get(`${url}/rating-change-30/${season}/${division}`, {
            params: { top }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching rating 3.0 change leaders:', error);
        throw error;
    }
};

export const fetchRoundSwingLeaders = async (
    season: number,
    division: string,
    top: boolean = true
): Promise<SwingResponse> => {
    try {
        const response = await axios.get(`${url}/round-swing/${season}/${division}`, {
            params: { top }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching round swing leaders:', error);
        throw error;
    }
};

export const fetchCTSideStats = async (
    season: number,
    division: string,
    top: boolean = true
): Promise<CTSideResponse> => {
    try {
        const response = await axios.get(`${url}/ct-sides/${season}/${division}`, {
            params: { top }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching CT side stats:', error);
        throw error;
    }
};

export const fetchTSideStats = async (
    season: number,
    division: string,
    top: boolean = true
): Promise<TSideResponse> => {
    try {
        const response = await axios.get(`${url}/t-sides/${season}/${division}`, {
            params: { top }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching T side stats:', error);
        throw error;
    }
};

export const fetchMultiKillStats = async (
    season: number,
    division: string,
    top: boolean = true
): Promise<MultiKillResponse> => {
    try {
        const response = await axios.get(`${url}/multi-killers/${season}/${division}`, {
            params: { top }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching multi-kill stats:', error);
        throw error;
    }
};